import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Animated,
  // eslint-disable-next-line no-restricted-imports
  Button,
  Easing,
  InputAccessoryView,
  Keyboard,
  KeyboardAvoidingView,
  View,
} from 'react-native'
import { useSelector } from 'react-redux'
import { Tooltip } from '@ui-kitten/components'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Icon, IconButton, Input, InputRef, Text } from '@components/base'
import { useUpdateDraftMeal } from '@screens/Meals/hooks'
import { loadingEffectsSelector } from '@selectors/loading'
import { mealDraftSelector } from '@selectors/meals'
import { Device } from '@src/config'
import { useVoice } from '@src/utils/voice'
import Storage from '@src/utils/storage'
import { userSelector } from '@src/selectors/app'

const HINT_HEIGHT = 66
const INPUT_HEIGHT = 80
const INPUT_NUMBER_OF_LINES = 3
const INPUT_HEIGHT_ON_FOCUS = 160
const INPUT_NUMBER_OF_LINES_ON_FOCUS = 6

interface MealDescriptionProps {
  editingDescription: boolean
  setEditingDescription: React.Dispatch<React.SetStateAction<boolean>>
}

export const MealDescription = ({
  editingDescription,
  setEditingDescription,
}: MealDescriptionProps) => {
  const styles = useStyleSheet(themedStyle)
  const updateDraftMeal = useUpdateDraftMeal()

  const height = useRef(new Animated.Value(HINT_HEIGHT)).current
  const inputHeight = useRef(new Animated.Value(INPUT_HEIGHT)).current

  const draftMeal = useSelector(mealDraftSelector)
  const user = useSelector(userSelector)
  const isGeneratingDescriptionFromImage = useSelector(loadingEffectsSelector)[
    'meals/getMealImageDescription'
  ]
  const voiceKey = `${Storage.USED_MEAL_LOG_VOICE_KEY}_${user?.id}`
  const hasNeverUsedVoice = !Storage.get(voiceKey)

  const [description, setDescription] = useState(draftMeal.description)
  const [numberOfLines, setNumberOfLines] = useState(3)
  const [voiceTooltipVisible, setVoiceTooltipVisible] = useState(false)
  const descriptionRef = useRef(description)
  const descriptionInputRef = useRef<InputRef>(null)

  const handleTranscript = useCallback(
    (newText: string) => {
      const description = descriptionRef.current
      const newDescription = description ? `${description} ${newText}` : newText
      descriptionRef.current = newDescription
      setDescription(newDescription)
      updateDraftMeal({ description: newDescription })
    },
    [updateDraftMeal],
  )

  const { isAvailable, isListening, startListening, stopListening } = useVoice(handleTranscript)

  useEffect(() => {
    setDescription(draftMeal.description)
  }, [draftMeal.description])

  const hasDescription = !!description

  useEffect(() => {
    Animated.timing(height, {
      toValue: hasDescription ? 0 : HINT_HEIGHT,
      duration: 200,
      easing: Easing.linear,
      useNativeDriver: false,
    }).start()
  }, [hasDescription, height])

  // Animate input expansion on focus
  useEffect(() => {
    const targetLines = editingDescription ? INPUT_NUMBER_OF_LINES_ON_FOCUS : INPUT_NUMBER_OF_LINES
    const targetHeight = editingDescription ? INPUT_HEIGHT_ON_FOCUS : INPUT_HEIGHT

    setNumberOfLines(targetLines)

    Animated.timing(inputHeight, {
      toValue: targetHeight,
      duration: 200,
      easing: Easing.out(Easing.quad),
      useNativeDriver: false,
    }).start()
  }, [editingDescription, inputHeight])

  const handleToggleMicrophone = useCallback(async () => {
    if (hasNeverUsedVoice) {
      Storage.set(voiceKey, 'true')
      setVoiceTooltipVisible(true)
      return
    }

    if (isListening) {
      descriptionInputRef.current?.blur()
      await stopListening()
    } else {
      descriptionInputRef.current?.focus()
      await startListening()
    }
  }, [hasNeverUsedVoice, voiceKey, isListening, startListening, stopListening])

  return (
    <KeyboardAvoidingView style={styles.container}>
      <View style={styles.headerContainer}>
        <Text type="regular" style={styles.label}>
          Describe your meal
        </Text>
        {isAvailable && (
          <Tooltip
            placement="left"
            visible={voiceTooltipVisible}
            onBackdropPress={() => {
              setVoiceTooltipVisible(false)
              handleToggleMicrophone()
            }}
            anchor={() => (
              <IconButton
                type="transparent"
                size="s"
                iconName="microphone"
                onPress={handleToggleMicrophone}
                accessibilityLabel="Describe your meal"
                style={[styles.microphoneButton, isListening && styles.activeMicrophoneButton]}
              />
            )}
          >
            <Text type="small" style={styles.tooltipText}>
              Easily log your meals by voice.
            </Text>
          </Tooltip>
        )}
      </View>
      {Device.ios && (
        <InputAccessoryView nativeID="Done">
          <View style={styles.accessory}>
            <Button onPress={() => Keyboard.dismiss()} accessibilityLabel="done" title="Done" />
          </View>
        </InputAccessoryView>
      )}
      <Animated.View
        style={{
          height: inputHeight,
        }}
      >
        <Input
          disabled={isGeneratingDescriptionFromImage}
          numberOfLines={numberOfLines}
          style={{ flex: 1 }}
          containerStyle={{ flex: 1 }}
          placeholder="Half a chicken breast, a side of mashed potatoes, and a can of coke"
          testID="AddMealComponent/Description"
          value={description}
          inputAccessoryViewID="Done"
          onChangeText={(description) => {
            setDescription(description)
            updateDraftMeal({ description }, true)
            descriptionRef.current = description
          }}
          onFocus={() => setEditingDescription(true)}
          onBlur={() => {
            setEditingDescription(false)
            if (isListening) {
              stopListening()
            }
          }}
          ref={descriptionInputRef}
        />
      </Animated.View>
      <Animated.View style={[styles.hint, { height }]}>
        <Icon name="sparkle" style={styles.sparkleIcon} />
        <Text type="regular" style={styles.hintText}>
          Automatically generate nutrition info by taking a photo or entering a description of your
          meal.
        </Text>
      </Animated.View>
    </KeyboardAvoidingView>
  )
}

const themedStyle = StyleService.create({
  container: {
    marginHorizontal: 16,
  },
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  accessory: {
    height: 48,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingHorizontal: 8,
    backgroundColor: 'theme.surface.base2',
  },
  label: {
    marginBottom: 8,
  },
  hint: {
    flexDirection: 'row',
    marginTop: 16,
    overflow: 'hidden',
  },
  sparkleIcon: {
    width: 32,
    height: 32,
    marginTop: 8,
    marginRight: 12,
    color: 'theme.accent.citron',
  },
  hintText: {
    flexShrink: 1,
    height: HINT_HEIGHT,
  },
  microphoneButton: {
    backgroundColor: 'theme.surface.base2',
    marginBottom: 8,
  },
  activeMicrophoneButton: {
    backgroundColor: 'theme.tooltip.link',
  },
  tooltipText: {
    color: 'theme.tooltip.textPrimary',
  },
})
